import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  Layers,
  ListChecks,
  MapPin,
  RefreshCw,
  UserX,
  XCircle,
} from 'lucide-react';
import type {
  ClassLevelsImportReport,
  EngineRun,
  FieldValueJobOutput,
  NativeValuesImportReport,
  UsersFinalizeReport,
} from '../types';

/**
 * Progression / résultat des étapes du moteur d'import ADM (une ligne par
 * étape, compteurs + erreurs).
 */
export function EngineStepsPanel({ runs, title }: { runs: EngineRun[]; title: string }) {
  if (runs.length === 0) return null;

  return (
    <Card>
      <CardHeader icon={<ListChecks />} iconColor="blue">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {runs.map((run) => (
          <div key={run.stepId} className="space-y-1 border-b pb-2 text-sm last:border-b-0 last:pb-0">
            <div className="flex flex-wrap items-center gap-2">
              {run.status === 'running' ? (
                <RefreshCw className="h-4 w-4 animate-spin text-blue-500" />
              ) : run.status === 'done' ? (
                <CheckCircle2 className="h-4 w-4 text-green-600" />
              ) : run.status === 'failed' ? (
                <XCircle className="h-4 w-4 text-destructive" />
              ) : (
                <Circle className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="font-medium">{run.name}</span>
              {run.report && (
                <span className="flex flex-wrap gap-1.5">
                  {run.report.created > 0 && (
                    <Badge variant="success">{run.report.created} créé(s)</Badge>
                  )}
                  {run.report.updated > 0 && (
                    <Badge variant="secondary">{run.report.updated} mis à jour</Badge>
                  )}
                  {run.report.skipped > 0 && (
                    <Badge variant="secondary">{run.report.skipped} déjà présent(s)</Badge>
                  )}
                  {run.report.failed > 0 && (
                    <Badge variant="destructive">{run.report.failed} en échec</Badge>
                  )}
                  {run.report.created === 0 &&
                    run.report.updated === 0 &&
                    run.report.skipped === 0 &&
                    run.report.failed === 0 && <Badge variant="secondary">rien à faire</Badge>}
                </span>
              )}
            </div>
            {run.status === 'running' && run.progress && run.progress.total > 0 && (
              <div className="flex items-center gap-3 pl-6">
                <Progress
                  value={Math.round((run.progress.done / run.progress.total) * 100)}
                  className="h-2 flex-1"
                />
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  {run.progress.done}/{run.progress.total} lignes
                </span>
              </div>
            )}
            {run.error && <p className="pl-6 text-xs text-destructive">{run.error}</p>}
            {run.report && (run.report.errors.length > 0 || run.report.warnings.length > 0) && (
              <ul className="list-inside list-disc pl-6 text-xs">
                {run.report.errors.map((e, i) => (
                  <li key={`e-${i}`} className="text-destructive">
                    {e}
                  </li>
                ))}
                {run.report.warnings.map((w, i) => (
                  <li key={`w-${i}`} className="text-amber-600 dark:text-amber-500">
                    {w}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const ROLE_LABELS: Record<string, string> = {
  student: 'Élèves',
  teacher: 'Enseignants',
  legal_guardian: 'Parents',
  director: 'Direction',
};

const isJobTerminal = (status: string) =>
  status === 'completed' || status === 'completed_with_errors' || status === 'failed';

/**
 * Suivi des passes post-import : jobs asynchrones de valeurs de champs
 * (champs perso + téléphones), reprise des téléphones/adresses, et
 * désactivation des utilisateurs créés.
 */
export function FieldValueJobsSummary({
  jobs,
  emails,
}: {
  jobs: Record<string, FieldValueJobOutput>;
  emails: Record<string, string>;
}) {
  const list = Object.values(jobs);
  if (list.length === 0) return null;

  const total = list.reduce((n, j) => n + j.totalSubmitted, 0);
  const success = list.reduce((n, j) => n + j.successCount, 0);
  const failed = list.reduce((n, j) => n + j.failedCount, 0);
  const pending = list.reduce((n, j) => n + j.pendingCount, 0);
  const running = list.some((j) => !isJobTerminal(j.status));
  const failedItems = list.flatMap((j) => j.failedItems ?? []);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {running ? (
          <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : null}
        <span className="font-medium">Valeurs de champs (traitement V2)</span>
        <Badge variant="secondary">{total} envoyée(s)</Badge>
        <Badge variant="success">{success} traitée(s)</Badge>
        {pending > 0 && <Badge variant="secondary">{pending} en cours</Badge>}
        {failed > 0 && <Badge variant="destructive">{failed} en échec</Badge>}
      </div>
      {total > 0 && (
        <div className="flex items-center gap-3">
          <Progress
            value={Math.round(((success + failed) / total) * 100)}
            className="h-2 flex-1"
          />
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {success + failed}/{total}
          </span>
        </div>
      )}
      {failedItems.length > 0 && (
        <ul className="list-inside list-disc text-xs text-destructive">
          {failedItems.slice(0, 15).map((item, i) => (
            <li key={`${item.userRoleId}-${i}`}>
              {emails[item.userRoleId] ?? item.userRoleId} :{' '}
              {item.errors.map((e) => e.message || e.code).join(' ; ')}
            </li>
          ))}
          {failedItems.length > 15 && <li>… et {failedItems.length - 15} autre(s)</li>}
        </ul>
      )}
    </div>
  );
}

/** Résultat de la reprise des téléphones/adresses (perdus par le pipeline V2). */
export function NativeValuesReport({ report }: { report: NativeValuesImportReport }) {
  const files = Object.entries(report.files);
  if (files.length === 0 && report.warnings.length === 0) return null;

  return (
    <Card>
      <CardHeader icon={<MapPin />} iconColor="blue">
        <CardTitle className="text-base">Téléphones et adresses</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Les colonnes <span className="font-mono">telephone</span> et{' '}
          <span className="font-mono">adresse</span> ne sont pas reprises par l’import V2 :
          elles sont rattachées ici aux champs natifs des fiches (adresses géocodées).
        </p>
        {report.warnings.map((w) => (
          <Alert key={w} variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{w}</AlertDescription>
          </Alert>
        ))}
        {files.map(([logicalName, file]) => {
          const errors = [
            ...(file.columns ?? []).flatMap((c) => c.errors),
            ...(file.address?.errors ?? []),
            ...(file.dates?.errors ?? []),
          ];
          return (
            <div key={logicalName} className="space-y-1 text-sm">
              <div className="flex items-center gap-2 font-medium">
                {file.label}
                <span className="font-normal text-muted-foreground">(fichier {logicalName})</span>
                {file.usersNotFound > 0 && (
                  <Badge variant="destructive">
                    {file.usersNotFound} utilisateur(s) non retrouvé(s)
                  </Badge>
                )}
              </div>
              {(file.columns ?? []).map((col) => (
                <div key={col.column} className="flex flex-wrap items-center gap-2">
                  <span className="text-muted-foreground">{col.label} :</span>
                  {(col.queued ?? 0) > 0 && (
                    <Badge variant="secondary">{col.queued} envoyée(s)</Badge>
                  )}
                  {col.created > 0 && <Badge variant="success">{col.created} créée(s)</Badge>}
                  {col.reused > 0 && <Badge variant="secondary">{col.reused} déjà présente(s)</Badge>}
                  {col.skipped > 0 && <Badge variant="secondary">{col.skipped} ignorée(s)</Badge>}
                </div>
              ))}
              {file.dates && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-muted-foreground">Dates de scolarité :</span>
                  {file.dates.updated > 0 && (
                    <Badge variant="success">{file.dates.updated} mise(s) à jour</Badge>
                  )}
                  {file.dates.skipped > 0 && (
                    <Badge variant="secondary">{file.dates.skipped} ignorée(s)</Badge>
                  )}
                  {file.dates.updated === 0 && file.dates.skipped === 0 && (
                    <Badge variant="secondary">aucune</Badge>
                  )}
                </div>
              )}
              {file.address && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-muted-foreground">Adresses :</span>
                  {file.address.created > 0 && <Badge variant="success">{file.address.created} créée(s)</Badge>}
                  {file.address.reused > 0 && <Badge variant="secondary">{file.address.reused} déjà présente(s)</Badge>}
                  {(file.address.unresolved ?? 0) > 0 && (
                    <Badge variant="destructive">{file.address.unresolved} non résolue(s)</Badge>
                  )}
                  {file.address.skipped > 0 && <Badge variant="secondary">{file.address.skipped} ignorée(s)</Badge>}
                </div>
              )}
              {errors.length > 0 && (
                <ul className="list-inside list-disc text-xs text-destructive">
                  {errors.slice(0, 15).map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                  {errors.length > 15 && <li>… et {errors.length - 15} autre(s)</li>}
                </ul>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

/** Résultat de la passe classes multi-niveaux (`code_niveau` = `CP|CE1`). */
export function ClassLevelsPanel({ report }: { report: ClassLevelsImportReport }) {
  if (report.updated === 0 && report.reused === 0 && report.skipped === 0 && report.errors.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader icon={<Layers />} iconColor="teal">
        <CardTitle className="text-base">Classes multi-niveaux</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          L’import V2 ne pose qu’un niveau par classe : les niveaux supplémentaires déclarés avec
          «&nbsp;|&nbsp;» dans <span className="font-mono">code_niveau</span> ont été rattachés
          après l’import.
        </p>
        <div className="flex flex-wrap gap-2 text-sm">
          {report.updated > 0 && <Badge variant="success">{report.updated} classe(s) complétée(s)</Badge>}
          {report.reused > 0 && <Badge variant="secondary">{report.reused} déjà complète(s)</Badge>}
          {report.skipped > 0 && <Badge variant="destructive">{report.skipped} introuvable(s)</Badge>}
        </div>
        {report.errors.length > 0 && (
          <ul className="list-inside list-disc text-xs text-destructive">
            {report.errors.slice(0, 15).map((e, i) => (
              <li key={i}>{e}</li>
            ))}
            {report.errors.length > 15 && <li>… et {report.errors.length - 15} autre(s)</li>}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Résultat de la désactivation post-import des utilisateurs créés. */
export function UsersFinalizePanel({ report }: { report: UsersFinalizeReport }) {
  const roles = Object.entries(report.roles);
  if (roles.length === 0 && report.warnings.length === 0) return null;

  const created = roles.reduce((n, [, r]) => n + r.created, 0);

  return (
    <Card>
      <CardHeader icon={<UserX />} iconColor="orange">
        <CardTitle className="text-base">Statut des utilisateurs créés</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          L’import V2 crée les utilisateurs actifs : ils ont été repassés en{' '}
          <span className="font-medium">inactif</span> pour qu’aucune invitation ne parte avant
          l’activation volontaire des comptes.
        </p>
        <div className="flex flex-wrap gap-2 text-sm">
          {roles.map(([roleCode, r]) => (
            <Badge key={roleCode} variant={r.failed > 0 ? 'destructive' : 'secondary'}>
              {ROLE_LABELS[roleCode] ?? roleCode} : {r.deactivated}/{r.created} désactivé(s)
            </Badge>
          ))}
        </div>
        {created === 0 && (
          <p className="text-sm text-muted-foreground">Aucun utilisateur créé par cet import.</p>
        )}
        {report.warnings.map((w) => (
          <Alert key={w} variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{w}</AlertDescription>
          </Alert>
        ))}
      </CardContent>
    </Card>
  );
}
